import React, { ComponentPropsWithoutRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CompanyCard from './CompanyCard';
import AutoComplete from 'react-google-autocomplete';
import LocationIcon from '../../assets/icons/LocationIcon';
import useTheme from '../../hooks/ui/useTheme';
import CloseIcon from '../../assets/icons/actions/CloseIcon';
import { GooglePlaceResource } from '../../services/resources/GooglePlaceResource';
import { CompanyDetailsInterface, Franchise } from '../../services/exports/Interfaces';
import collect from 'collect.js';
import { useLocationData } from './provider/LocationDataProvider';
import DeliveryAddressForm from './form/DeliveryAddressForm';
import { ORDER_METHODS } from '../../services/exports/Constants';
import useDeliveryCompatibleStuff from './useDeliveryCompatibleStuff';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import LoadingIndicator from '../../components-library/loading/LoadingIndicator';
import { useFormContext } from 'react-hook-form';
import { DeliveryAddressSchema } from '../../modules/mobile/modals/delivery-address/form/schema';
import InfoIcon from '../../assets/logo/InfoIcon';
import classnames from 'classnames';
import useEnv from '../../hooks/utility/useEnv';
import { ValidationErrors } from './OrderMethodModal';

export interface CompanyDistanceInterface {
  company: CompanyDetailsInterface;
  distance?: number;
}

interface Props extends ComponentPropsWithoutRef<'section'> {
  data: Franchise;
  addressSearch: string;
  locationSwitchDisabled: boolean;
  setAddressSearch: (value: string) => void;
  validationError?: ValidationErrors;
}

export default function CompaniesList(props: Props) {
  const { data, addressSearch, className, locationSwitchDisabled, setAddressSearch, ...rest } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:CompaniesList' });
  const { t: tErrors } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:OrderMethodModal' });
  const deliveryAddressForm = useFormContext<DeliveryAddressSchema>();
  const { orderMethod, validationError, setCompany, company } = useLocationData();
  const { isSubmitSuccessful, isSubmitting } = deliveryAddressForm.formState;
  const theme = useTheme();
  const { VITE_GOOGLE_MAPS_API_KEY } = useEnv();
  const [place, setPlace] = useState<GooglePlaceResource>(null);
  const { queryState, isLoading, handle } = useDeliveryCompatibleStuff();
  const hasDeliveryAddressForm =
    orderMethod === ORDER_METHODS.DELIVERY && place && !isSubmitSuccessful && !isSubmitting;
  const { isOrderMethodInfoComplete } = useOrderFlow();
  const deliveryCompatibleCompanies = queryState.data;

  const companies = useMemo<CompanyDistanceInterface[]>(
    () =>
      (orderMethod !== ORDER_METHODS.DELIVERY || place) &&
      collect(data?.companies ?? [])
        .when(
          orderMethod === ORDER_METHODS.DELIVERY,
          (companies) => companies.where('delivery_schedule.length', '!==', 0),
          (companies) => companies.where('work_schedule.length', '!==', 0),
        )
        .when(orderMethod === ORDER_METHODS.DELIVERY, (companies) =>
          companies.whereIn('id', deliveryCompatibleCompanies ?? []),
        )
        .when(orderMethod === ORDER_METHODS.ROOM_SERVICE, (companies) =>
          companies.filter((company) => company?.has_room_service),
        )
        .when(data?.companies?.length > 1, (companies) => companies.where('has_live_orders', true))
        .map((item) => {
          const distance = place?.calculateDistance({
            lat: item?.lat,
            lng: item?.lon,
          });

          return {
            company: item,
            distance,
          };
        })
        .sortBy('distance')
        .toArray(),
    [data?.companies, place, deliveryCompatibleCompanies, orderMethod],
  );

  const onPlaceSelected = async (placeResult: google.maps.places.PlaceResult): Promise<void> => {
    const selectedPlaceResource = new GooglePlaceResource(placeResult);

    deliveryAddressForm.setValue('delivery_street_name', selectedPlaceResource.getStreetName());
    deliveryAddressForm.setValue('delivery_street_number', selectedPlaceResource.getStreetNumber());
    deliveryAddressForm.setValue('delivery_city', selectedPlaceResource.getCity());
    deliveryAddressForm.setValue('delivery_zip_code', selectedPlaceResource.getPostalCode());
    deliveryAddressForm.setValue('delivery_lat', selectedPlaceResource.getLat());
    deliveryAddressForm.setValue('delivery_lng', selectedPlaceResource.getLng());

    setAddressSearch(selectedPlaceResource.formatAddress());
    setPlace(selectedPlaceResource);

    await handle(selectedPlaceResource);
  };
  const clearAddress = () => {
    deliveryAddressForm.reset({
      delivery_lng: undefined,
      delivery_street_number: '',
      delivery_zip_code: '',
      delivery_lat: undefined,
      delivery_city: '',
      delivery_street_name: '',
    });
    setAddressSearch('');
    setPlace(null);
    setCompany(null);
  };

  useEffect(() => {
    if (!place) {
      return;
    }

    handle(place);
  }, [place, orderMethod]);

  useEffect(() => {
    async function setInitialPlace(): Promise<void> {
      if (
        !isOrderMethodInfoComplete ||
        !deliveryAddressForm?.getValues('delivery_lat') ||
        !deliveryAddressForm?.getValues('delivery_lng')
      ) {
        return;
      }

      const { success, location } = await GooglePlaceResource.getPlaceFromCoordinates({
        lat: deliveryAddressForm?.getValues('delivery_lat'),
        lng: deliveryAddressForm?.getValues('delivery_lng'),
      });

      if (!success) {
        return;
      }

      const locationResource = new GooglePlaceResource(location);

      setPlace(locationResource);
      setAddressSearch(locationResource.formatAddress());
    }

    setInitialPlace();
  }, []);

  useEffect(() => {
    if (orderMethod !== ORDER_METHODS.DELIVERY) {
      return;
    }

    if (!place || isSubmitSuccessful) {
      return;
    }

    handle(place);
  }, [orderMethod]);

  const scrollToCompany = (id: number): void => {
    const element = document.getElementById(`company-${id}`);
    if (!element) return;
    element.scrollIntoView({ behavior: 'auto', block: 'start' });
  };

  useLayoutEffect(() => {
    if (!company?.id || companies?.length === 0 || isLoading) return;
    scrollToCompany(company?.id);
  }, [companies, isLoading]);

  const searchPlaceholder = t(
    orderMethod === ORDER_METHODS.DELIVERY ? 'form.address.delivery.placeholder' : 'form.address.placeholder',
  );
  const hasNoDeliveryCompaniesLabel =
    orderMethod === ORDER_METHODS.DELIVERY &&
    !queryState.isUninitialized &&
    !queryState.isLoading &&
    (!queryState?.data?.length || !companies?.length);

  const hasDeliveryCompaniesSearch = useMemo(() => {
    if (data?.has_multiple_companies || orderMethod === ORDER_METHODS.PICKUP || !data?.companies?.length) {
      return true;
    }
    const company = collect(data?.companies).first();
    return company?.has_delivery && company?.delivery_enabled && collect(company?.delivery_zones).isNotEmpty();
  }, [data?.companies, data?.has_multiple_companies, orderMethod]);

  const ErrorLabel = ({ message }: { message: string }): JSX.Element | null => {
    return (
      <p className="bg-[#BC4C6B26]/15 px-sm py-[10px] rounded-lg text-[#BC4C6B] font-semibold text-sm flex items-center gap-x-sm">
        <InfoIcon color="#BC4C6B" />
        {message}
      </p>
    );
  };

  const renderSearchBar = (): JSX.Element | null => {
    if (orderMethod === ORDER_METHODS.PICKUP && !data?.has_multiple_companies) {
      return null;
    }

    if (locationSwitchDisabled && orderMethod === ORDER_METHODS.PICKUP) {
      return null;
    }

    if (!hasDeliveryCompaniesSearch) {
      return null;
    }

    return (
      <div className="flex items-center w-full px-md py-sm bordered space-x-md">
        {isLoading ? (
          <LoadingIndicator className="size-2" size={16} />
        ) : (
          <LocationIcon className="size-4" color={theme.color.brand.icon.color} />
        )}
        <AutoComplete
          apiKey={VITE_GOOGLE_MAPS_API_KEY}
          onPlaceSelected={onPlaceSelected}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddressSearch(e.target.value)}
          value={addressSearch}
          placeholder={searchPlaceholder}
          className="w-full text-sm overflow-ellipsis placeholder:text-[#777777]"
          options={{
            types: data?.country === 'GB' ? ['street_address', 'postal_code'] : ['address'],
            componentRestrictions: {
              country: data?.country,
            },
          }}
          key={data?.country}
          onError={(e) => console.error('Google place autocomplete error:', e)}
        />
        {!!addressSearch?.length && (
          <div
            className="bg-gray-700 rounded-full flex flex-none justify-center items-center size-4 cursor-pointer"
            onClick={clearAddress}
          >
            <CloseIcon className="size-2" color={theme.color.gray['500']} />
          </div>
        )}
      </div>
    );
  };

  const renderError = (): JSX.Element | null => {
    if (hasNoDeliveryCompaniesLabel) {
      return <ErrorLabel message={t('errors.no_delivery_companies')} />;
    }

    if (!hasDeliveryCompaniesSearch) {
      return <ErrorLabel message={t('errors.delivery_not_available')} />;
    }

    if (props.validationError) {
      return <ErrorLabel message={tErrors(`error_messages.${props.validationError}`)} />;
    }

    return null;
  };

  return (
    <>
      <section className={classnames('space-y-md flex flex-col', className)} {...rest}>
        {renderSearchBar()}
        {renderError()}
        {hasDeliveryAddressForm && <DeliveryAddressForm />}
        {validationError && <p className="text-danger text-sm">{tErrors(`error_messages.${validationError}`)}</p>}
        {!isLoading &&
          companies?.map((item, index) => (
            <div key={`${item.company?.id}${orderMethod}`} id={`company-${item.company?.id}`}>
              <CompanyCard
                data={item}
                className={classnames('pt-md', !!index && 'border-t')}
                showRadioButton={!locationSwitchDisabled}
              />
            </div>
          ))}
      </section>
    </>
  );
}
